import {NgClass} from '@angular/common';
import {AfterViewChecked, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild} from '@angular/core';

@Component({
  selector: 'app-ellipsis',
  templateUrl: './ellipsis.component.html',
  styleUrls: ['./ellipsis.component.scss'],
  standalone: false
})
export class EllipsisComponent implements OnChanges, AfterViewChecked {
  @Input() text: string;
  @Input() textHiddenClass: NgClass['ngClass'];
  @Input() textUnhiddenClass: NgClass['ngClass'];
  @Input() hideButtons = false;
  @Input() collapsed = true;
  @Input() canToggle = true;
  @Input() expandByDefault = false;

  /**
   * Emits when the user clicks the ... or Show Less links to collapse or expand the text.
   */
  @Output() collapsedChange = new EventEmitter<boolean>();
  /**
   * Emits whenever the text changes to/from the collapsed state for any reason, e.g. the browser window is resized, or
   * the user decided to expand or collapse the text.
   */
  @Output() textTruncatedChange = new EventEmitter<boolean>();

  @ViewChild('textContainer') textContainer: ElementRef<HTMLElement> | null;
  @ViewChild('noPostContentEndMarker') noPostContentEndMarker: ElementRef<HTMLElement> | null;
  @ViewChild('postContentContainer') postContentContainer: ElementRef<HTMLElement> | null;
  @ViewChild('endMarker') endMarker: ElementRef<HTMLElement> | null;

  textTruncated = false;
  private expandByDefaultTriggered = false;
  private afterRenderFn: (() => void) | null = null

  constructor(
    private cdr: ChangeDetectorRef
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.text) {
      this.afterRenderFn = () => {
        const textTruncatedUpdated = this.updateTextTruncated();
        if (textTruncatedUpdated) {
          this.cdr.detectChanges();
        }
      };
    }
    if (changes?.expandByDefault) {
      if (changes.expandByDefault.firstChange && this.expandByDefault) {
        this.collapsed = false;
      }
      this.checkAndExpandByDefault();
    }
  }

  ngAfterViewChecked(): void {
    if (this.afterRenderFn) {
      this.afterRenderFn();
      this.afterRenderFn = null;
    }
  }

  setCollapsed(collapsed: boolean, event?: Event) {
    event?.stopPropagation();
    if (this.collapsed !== collapsed) {
      this.collapsed = collapsed;
      this.collapsedChange.emit(collapsed);
      if (event) {
        // calling detectChanges() because stopPropagation stops auto change detection.
        this.cdr.detectChanges();
      }
    }
  }

  /** While collapsed, textContainer has `white-space: nowrap` and if `scrollWidth > offsetWidth`, it means it would have wrapped if allowed.
   * While expanded, if endMarker element `offsetTop` value is bigger than `offsetTop` of textContainer, it means it wrapped */
  updateTextTruncated(): boolean {
    if (this.textContainer) {
      const textTruncated = this.collapsed
        ? this.textContainer.nativeElement.scrollWidth > this.textContainer.nativeElement.offsetWidth
        : this.getEndMarker().nativeElement.offsetTop > this.textContainer.nativeElement.offsetTop;
      if (this.textTruncated !== textTruncated) {
        this.textTruncated = textTruncated;
        this.textTruncatedChange.emit(this.textTruncated);
        return true;
      }
    }
    return false;
  }

  private getEndMarker(): ElementRef<HTMLElement> {
    return this.postContentContainer.nativeElement.childElementCount
      ? this.endMarker
      : this.noPostContentEndMarker;
  }

  private checkAndExpandByDefault() {
    if (!this.expandByDefaultTriggered && this.expandByDefault) {
      this.expandByDefaultTriggered = true;
      this.setCollapsed(false);
    }
  }
}
